import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// Define the shape of our authentication context
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
  getAuthToken: () => string | null;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

interface AuthTokenData {
  token: string;
  csrfToken: string;
  expiresAt: number; // Timestamp when the token expires
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: false,
  error: null,
  login: async () => false,
  logout: () => {},
  getAuthToken: () => null,
  fetchWithAuth: async () => new Response(null, { status: 401 }),
});

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

// Generate a CSRF token
function generateCSRFToken(): string {
  const randomValues = new Uint8Array(16);
  
  // Use crypto.getRandomValues if available, otherwise fallback to Math.random
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(randomValues);
  } else {
    for (let i = 0; i < 16; i++) {
      randomValues[i] = Math.floor(Math.random() * 256);
    }
  }
  
  return Array.from(randomValues)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Start with loading state
  const [error, setError] = useState<string | null>(null);
  const [authData, setAuthData] = useState<AuthTokenData | null>(null);

  // Check authentication status on initial load
  useEffect(() => {
    // Auto-authenticate on localhost for development
    const isLocalhost = 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1';
    
    if (isLocalhost) {
      // Bypass authentication for local development
      console.log('Development environment detected - bypassing authentication');
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }
    
    // For production, check auth data from local storage
    const storedAuthData = localStorage.getItem('auth_data');
    
    if (storedAuthData) {
      try {
        const parsedAuthData: AuthTokenData = JSON.parse(storedAuthData);
        
        // Check if token is still valid (not expired)
        if (parsedAuthData.expiresAt > Date.now()) {
          setIsAuthenticated(true);
          setAuthData(parsedAuthData);
        } else {
          // Token expired, remove it
          localStorage.removeItem('auth_data');
        }
      } catch (err) {
        // Invalid stored data
        localStorage.removeItem('auth_data');
      }
    }
    
    // Finished loading regardless of outcome
    setIsLoading(false);
  }, []);

  // Function to get current auth token for API calls
  const getAuthToken = (): string | null => {
    if (!authData) {
      const storedData = localStorage.getItem('auth_data');
      if (storedData) {
        try {
          const parsed: AuthTokenData = JSON.parse(storedData);
          if (parsed.expiresAt > Date.now()) {
            return parsed.token;
          } else {
            // Token expired, remove it
            localStorage.removeItem('auth_data');
          }
        } catch (e) {
          return null;
        }
      }
      return null;
    }
    return authData.token;
  };

  // Login function using API
  const login = async (username: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Generate a CSRF token for this login request
      const csrfToken = generateCSRFToken();
      
      // Call the API authentication endpoint with CSRF token
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ 
          username, // Include username in the request
          password,
          csrfToken // Send CSRF token in the request body
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Extract token expiration from the token if possible
        // For our custom token, we'll rely on a 24 hour validity
        const expirationTime = Date.now() + (24 * 60 * 60 * 1000);
        
        // Store token, CSRF token and expiration
        const newAuthData: AuthTokenData = {
          token: data.token,
          csrfToken: csrfToken,
          expiresAt: expirationTime
        };
        
        // Update state and localStorage
        setAuthData(newAuthData);
        localStorage.setItem('auth_data', JSON.stringify(newAuthData));
        setIsAuthenticated(true);
        setIsLoading(false);
        return true;
      } else {
        setError(data.message || 'Authentication failed');
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network or server error');
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_data');
    setIsAuthenticated(false);
    setAuthData(null);
  };

  // The context value that will be provided
  // Implementation of fetchWithAuth within the context
  const fetchWithAuthImpl = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const token = getAuthToken();
    return fetchWithAuth(url, token, options);
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    getAuthToken,
    fetchWithAuth: fetchWithAuthImpl,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper for making authenticated API calls
export const fetchWithAuth = async (
  url: string, 
  token: string | null, 
  options: RequestInit = {}
): Promise<Response> => {
  if (!token) {
    throw new Error('Authentication required');
  }
  
  // Clone the options to avoid modifying the original
  const authOptions = { ...options };
  
  // Add Authorization header with token
  authOptions.headers = {
    ...authOptions.headers,
    'Authorization': `Bearer ${token}`
  };
  
  return fetch(url, authOptions);
};

export default AuthProvider;
